import React, { useState, useRef, useCallback, useEffect } from 'react';
// ...existing code...
// Ajout de la clé d'accueil dans le JSON si elle n'existe pas déjà
// (à ajouter dans en.json : "welcome.subtitle": "How can I help you today?" )
import en from './languages/en.json';
import fr from './languages/fr.json';
import es from './languages/es.json';
import de from './languages/de.json';
import it from './languages/it.json';
import nl from './languages/nl.json';
import pt from './languages/pt.json';
import ru from './languages/ru.json';
import ar from './languages/ar.json';
import zh from './languages/zh.json';
import hi from './languages/hi.json';
import bn from './languages/bn.json';
import ja from './languages/ja.json';
import tr from './languages/tr.json';
import ko from './languages/ko.json';
import vi from './languages/vi.json';
import ur from './languages/ur.json';
import pa from './languages/pa.json';
import ModelDropdownMenu from './ModelDropdownMenu';
import ChatInput from './ChatInput';
import MessagesList from './MessagesList';
import BannedUserModal from './BannedUserModal';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from './supabaseClient';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import './App.css';
import './Mobile.css';
import './LoginModal.css';

// Mapping des locales Discord vers nos codes de langue
function mapDiscordLocaleToLanguage(discordLocale) {
  if (!discordLocale) return null;
  
  const localeMap = {
    'en-US': 'en',
    'en-GB': 'en',
    'fr': 'fr',
    'es-ES': 'es',
    'es-419': 'es', // Spanish (Latin America)
    'de': 'de',
    'it': 'it',
    'nl': 'nl',
    'pt-BR': 'pt',
    'ru': 'ru',
    'ar': 'ar',
    'zh-CN': 'zh',
    'zh-TW': 'zh',
    'hi': 'hi',
    'bn': 'bn',
    'ja': 'ja',
    'tr': 'tr',
    'ko': 'ko'
  };
  
  return localeMap[discordLocale] || null;
}

// État global pour la langue
let currentLang = 'en';
let translations = { en, fr, es, de, it, nl, pt, ru, ar, zh, hi, bn, ja, tr, ko, vi, ur, pa };

// Fonction utilitaire pour accéder aux textes du JSON de langue
export function t(path) {
  const dict = translations[currentLang] || translations['en'];
  return path.split('.').reduce((obj, key) => (obj && obj[key] !== undefined ? obj[key] : path), dict);
}

// Fonction pour changer la langue globalement
function setGlobalLanguage(lang) {
  currentLang = lang;
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem('lang', lang);
  }
}

// Sidebar avec menu d'options (3 points) au hover
function Sidebar({ user, conversations, onSelect, onCreate, selectedId, onSearch, searchValue, setSearchValue, onRename, onDelete, titleLoadingId, className = '', selectedModel, onModelSelect, lang, onLanguageChange, isMobile = false }) {
  const [menuOpenId, setMenuOpenId] = React.useState(null);
  const [editingId, setEditingId] = React.useState(null);
  const [editValue, setEditValue] = React.useState("");
  const [showRenameModal, setShowRenameModal] = React.useState(false);
  const menuRef = React.useRef();

  // Fermer le menu au clic extérieur
  React.useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpenId(null);
      }
    }
    if (menuOpenId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpenId]);
  // Tri des conversations par période
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const lastWeek = new Date(now);
  lastWeek.setDate(now.getDate() - 7);
  const lastMonth = new Date(now);
  lastMonth.setMonth(now.getMonth() - 1);

  const groups = {
    [t('sidebar.yesterday')]: [],
    [t('sidebar.lastWeek')]: [],
    [t('sidebar.lastMonth')]: [],
    [t('sidebar.longAgo')]: []
  };
  conversations.forEach(conv => {
    const date = new Date(conv.updated_at);
    if (date > yesterday) {
      groups[t('sidebar.yesterday')].push(conv);
    } else if (date > lastWeek) {
      groups[t('sidebar.lastWeek')].push(conv);
    } else if (date > lastMonth) {
      groups[t('sidebar.lastMonth')].push(conv);
    } else {
      groups[t('sidebar.longAgo')].push(conv);
    }
  });

  return (
    <div className={`sidebar ${className}`}>
      <div className="sidebar-header">
        <h2 className="sidebar-title">{t('sidebar.title')}</h2>
        <button className="sidebar-new-btn" onClick={onCreate}>{t('sidebar.newConversation')}</button>
      </div>
      
      {/* Settings section for mobile */}
      {isMobile && (
        <div className="sidebar-settings">
          <div className="sidebar-settings-title">{t('sidebar.settings') || 'Paramètres'}</div>
          
          {/* Model selector */}
          <div className="sidebar-setting-item">
            <label className="sidebar-setting-label">{t('sidebar.model') || 'Modèle'}</label>
            <ModelDropdownMenu
              selectedModel={selectedModel}
              onSelect={onModelSelect}
              motionProps={{}}
              isMobile={true}
            />
          </div>
          
          {/* Language selector */}
          <div className="sidebar-setting-item">
            <label className="sidebar-setting-label">{t('sidebar.language') || 'Langue'}</label>
            <select 
              value={lang} 
              onChange={(e) => onLanguageChange(e.target.value)} 
              className="sidebar-language-select"
            >
              <option value="fr">{t('language.french')}</option>
              <option value="en">{t('language.english')}</option>
              <option value="es">{t('language.spanish')}</option>
              <option value="de">{t('language.german')}</option>
              <option value="it">{t('language.italian')}</option>
              <option value="nl">{t('language.dutch')}</option>
              <option value="pt">{t('language.portuguese')}</option>
              <option value="ru">{t('language.russian')}</option>
              <option value="ar">{t('language.arabic')}</option>
              <option value="zh">{t('language.mandarin')}</option>
              <option value="hi">{t('language.hindi')}</option>
              <option value="bn">{t('language.bengali')}</option>
              <option value="ja">{t('language.japanese')}</option>
              <option value="tr">{t('language.turkish')}</option>
              <option value="ko">{t('language.korean')}</option>
              <option value="vi">{t('language.vietnamese')}</option>
              <option value="ur">{t('language.urdu')}</option>
              <option value="pa">{t('language.punjabi')}</option>
            </select>
          </div>
        </div>
      )}
      <div className="sidebar-search">
        <input
          type="text"
          placeholder={t('sidebar.searchPlaceholder')}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
        />
      </div>
      <div className="sidebar-list">
        {Object.entries(groups).every(([_, arr]) => arr.length === 0) ? (
          <div className="sidebar-empty">{t('sidebar.empty')}</div>
        ) : (
          Object.entries(groups).map(([label, arr]) => (
            arr.length > 0 && (
              <div key={label}>
                <div style={{ fontWeight: 600, color: '#bbb', fontSize: 15, margin: '12px 0 4px 0' }}>{label}</div>
                {arr.map(conv => (
                  <div
                    key={conv.id}
                    className={`sidebar-item${conv.id === selectedId ? ' selected' : ''}`}
                    onClick={() => onSelect(conv)}
                    style={{ position: 'relative' }}
                  >
                    {/* Affichage normal, plus d'input inline */}
                    <>
                      {titleLoadingId === conv.id ? (
                        <div className="sidebar-title-skeleton" style={{width: '80%', height: 22, borderRadius: 8, background: 'linear-gradient(90deg,#eee 25%,#ddd 50%,#eee 75%)', marginBottom: 4, animation: 'skeleton-loading 1.2s infinite'}}></div>
                      ) : (
                        <div className="sidebar-item-title">{conv.title || t('sidebar.empty')}</div>
                      )}
                      <button
                        className="sidebar-menu-btn"
                        onClick={e => { e.stopPropagation(); setMenuOpenId(menuOpenId === conv.id ? null : conv.id); }}
                        tabIndex={-1}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="5" cy="12" r="2" fill="#888"/><circle cx="12" cy="12" r="2" fill="#888"/><circle cx="19" cy="12" r="2" fill="#888"/></svg>
                      </button>
                      <AnimatePresence>
                        {menuOpenId === conv.id && (
                          <motion.div
                            ref={menuRef}
                            className="sidebar-menu-popup sidebar-menu-blur"
                            initial={{ opacity: 0, scale: 0.8, y: -10, backdropFilter: 'blur(0px)', WebkitBackdropFilter: 'blur(0px)' }}
                            animate={{ opacity: 1, scale: 1, y: 0, backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', transition: { type: 'spring', stiffness: 400, damping: 22 } }}
                            exit={{ opacity: 0, scale: 0.8, y: -10, backdropFilter: 'blur(0px)', WebkitBackdropFilter: 'blur(0px)', transition: { duration: 0.18 } }}
                            style={{ position: 'absolute', right: 36, top: 32, border: '1px solid #333', borderRadius: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.18)', zIndex: 1000, minWidth: 140, transformOrigin: 'top right' }}
                          >
                            <button
                              className="sidebar-menu-item"
                              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 'none', padding: '8px 12px', cursor: 'pointer', color: '#fff' }}
                              onClick={e => { e.stopPropagation(); setEditingId(conv.id); setEditValue(conv.title || ''); setMenuOpenId(null); setShowRenameModal(true); }}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M4 21h4l11-11-4-4L4 17v4z" stroke="#bbb" strokeWidth="2"/></svg>
                              {t('sidebar.rename')}
                            </button>
                            <button
                              className="sidebar-menu-item"
                              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 'none', padding: '8px 12px', cursor: 'pointer', color: '#e74c3c' }}
                              onClick={e => { e.stopPropagation(); onDelete(conv.id); setMenuOpenId(null); }}
                            >
                              {/* Icône poubelle Material Design */}
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M3 6h18" stroke="#e74c3c" strokeWidth="2"/><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="#e74c3c" strokeWidth="2"/><rect x="5" y="8" width="14" height="12" rx="2" stroke="#e74c3c" strokeWidth="2"/><path d="M10 12v6M14 12v6" stroke="#e74c3c" strokeWidth="2"/></svg>
                              {t('sidebar.delete')}
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                    {/* Popup modale de renommage */}
                    <AnimatePresence>
                      {showRenameModal && editingId === conv.id && (
                        <motion.div
                          className="rename-modal-overlay"
                          initial={{ opacity: 0, backdropFilter: 'blur(0px)', WebkitBackdropFilter: 'blur(0px)' }}
                          animate={{ opacity: 1, backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', transition: { duration: 0.28 } }}
                          exit={{ opacity: 0, backdropFilter: 'blur(0px)', WebkitBackdropFilter: 'blur(0px)', transition: { duration: 0.18 } }}
                          style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.35)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}}
                        >
                          <motion.div
                            className="rename-modal-card"
                            initial={{ opacity: 0, scale: 0.7 }}
                            animate={{ opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 420, damping: 22, duration: 0.44 } }}
                            exit={{ opacity: 0, scale: 0.7, transition: { duration: 0.22 } }}
                            style={{background:'#23242a',borderRadius:20,padding:'0',minWidth:480,maxWidth:540,boxShadow:'0 8px 48px rgba(0,0,0,0.32)',display:'flex',flexDirection:'column',alignItems:'stretch',overflow:'hidden',transformOrigin:'center'}}
                          >
                            <div style={{padding:'38px 48px 32px 48px',display:'flex',flexDirection:'column',gap:20}}>
                              <h3 style={{color:'#fff',margin:0,fontSize:25,fontWeight:600,textAlign:'center',letterSpacing:'-0.5px'}}>{t('renameModal.title')}</h3>
                              <div style={{color:'#bbb',fontSize:16,textAlign:'center',marginTop:-10,marginBottom:10,lineHeight:1.5}}>
                                {t('renameModal.description').split('\n').map((line, i) => <React.Fragment key={i}>{line}<br/></React.Fragment>)}
                              </div>
                              <input
                                type="text"
                                value={editValue}
                                autoFocus
                                onChange={e => setEditValue(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    if (editValue.trim()) {
                                      onRename(editingId, editValue);
                                      setEditingId(null);
                                      setShowRenameModal(false);
                                    }
                                  } else if (e.key === 'Escape') {
                                    setEditingId(null);
                                    setShowRenameModal(false);
                                  }
                                }}
                                style={{fontSize:19,padding:'13px 18px',borderRadius:9,border:'1.5px solid #2c2d33',background:'#18191c',color:'#fff',marginTop:6,marginBottom:6,outline:'none',fontWeight:500,boxShadow:'0 1px 4px rgba(0,0,0,0.08)'}}
                                placeholder={t('renameModal.placeholder')}
                              />
                              <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:8}}>
                                <motion.button
                                  onClick={() => { setEditingId(null); setShowRenameModal(false); }}
                                  style={{background:'#23242a',color:'#bbb',border:'none',borderRadius:7,padding:'10px 18px',fontSize:16,cursor:'pointer',fontWeight:500,transition:'background 0.2s',boxShadow:'none',minWidth:90,textAlign:'center'}}
                                  whileTap={{ scale: 0.95 }}
                                >{t('renameModal.cancel')}</motion.button>
                                <motion.button
                                  onClick={() => { if (editValue.trim()) { onRename(editingId, editValue); setEditingId(null); setShowRenameModal(false); } }}
                                  style={{background:'#E36C48',color:'#fff',border:'none',borderRadius:7,padding:'10px 18px',fontSize:16,cursor:'pointer',fontWeight:500,boxShadow:'0 2px 8px rgba(227,108,72,0.12)',transition:'background 0.2s',minWidth:90,textAlign:'center'}}
                                  whileTap={{ scale: 0.95 }}
                                  whileHover={{ backgroundColor: '#CD6546' }}
                                >{t('renameModal.confirm')}</motion.button>
                              </div>
                            </div>
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            )
          ))
        )}
      </div>
    </div>
  );
}

// Nouvelle popup de connexion inspirée du design fourni
function LoginModal({ open, onClose, onLogin }) {
  if (!open) return null;
  return (
    <div className="login-modal-overlay">
      <div className="login-modal-card">
        <div className="login-modal-img">
          <img src={require('./img/login-image.png')} alt="Connexion Illustration" width={313} height={583} style={{objectFit:'cover', borderRadius:'16px 0 0 16px'}} />
        </div>
        <div className="login-modal-content">
          <h1>{t('loginModal.title')}</h1>
          <p>{t('loginModal.description')}</p>
          <button className="login-btn-main" onClick={onLogin}>{t('loginModal.login')}</button>
          <button className="close-btn-main" onClick={onClose}>{t('loginModal.cancel')}</button>
        </div>
      </div>
    </div>
  );
}

// Popup de consentement pour les conditions d'utilisation et politique de confidentialité
function ConsentModal({ open, onAccept }) {
  if (!open) return null;
  
  return (
    <div className="consent-modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.75)',
      zIndex: 3000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)'
    }}>
      <div className="consent-modal-card" style={{
        background: '#23242a',
        borderRadius: 16,
        padding: '40px',
        maxWidth: 500,
        margin: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        textAlign: 'center'
      }}>
        <h2 style={{
          color: '#fff',
          fontSize: 24,
          fontWeight: 600,
          marginBottom: 20,
          marginTop: 0
        }}>
          Terms & Privacy
        </h2>
        
        <p style={{
          color: '#bbb',
          fontSize: 16,
          lineHeight: 1.6,
          marginBottom: 30
        }}>
          By using VolcanAI, you agree to our{' '}
          <a 
            href="/terms.html" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#E36C48', textDecoration: 'none' }}
          >
            Terms of Service
          </a>
          {' '}and{' '}
          <a 
            href="/privacy.html" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#E36C48', textDecoration: 'none' }}
          >
            Privacy Policy
          </a>
          . Please read them carefully before continuing.
        </p>
        
        <button 
          onClick={onAccept}
          style={{
            background: '#E36C48',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '12px 24px',
            fontSize: 16,
            fontWeight: 500,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(227,108,72,0.2)',
            transition: 'all 0.2s',
            width: '100%'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#CD6546'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#E36C48'}
        >
          I acknowledge that I have read and accept
        </button>
      </div>
    </div>
  );
}

// Fonction utilitaire pour convertir un File en base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

// Fonction pour vérifier si l'utilisateur est banni via l'API
async function checkBannedUserAPI(user, session) {
  if (!user || !session) return false;
  
  try {
    const response = await fetch('/api/check-ban', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      console.error('Error checking ban status:', response.status);
      return false;
    }
    
    const data = await response.json();
    // console.log('Ban check result:', data);
    
    return data.isBanned || false;
  } catch (error) {
    console.error('Error checking ban status:', error);
    return false;
  }
}

function App() {
  // All state declarations at the top to avoid ReferenceError
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showBannedModal, setShowBannedModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]); // Array of File
  const [filePreviews, setFilePreviews] = useState([]); // Array of preview URLs or null
  const [messages, setMessages] = useState([]);
  // Fix: Add state for currentConversationId
  const [currentConversationId, setCurrentConversationId] = useState(null);
  // Added: global state for rename popup
  const [showRenameModal, setShowRenameModal] = useState(false);
  // Added: state for selected model
  const [selectedModel, setSelectedModel] = useState({
    displayName: 'Gemini 2.5 Flash',
    description: 'For the most advanced tasks.',
    codeName: 'gemini-2.5-flash',
    thinking: true,
  });
  // Added: state for user menu selection
  const [selectedMenu, setSelectedMenu] = useState(null);
  // Added: state for language
  const [lang, setLang] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const savedLang = window.localStorage.getItem('lang') || 'en';
      currentLang = savedLang;
      return savedLang;
    }
    return 'en';
  });

  // Mobile-specific states
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Terms of Service and Privacy Policy consent
  const [showConsentModal, setShowConsentModal] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      return !window.localStorage.getItem('termsAccepted');
    }
    return false;
  });

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    if (!isMobile) return;
    
    const handleClickOutside = (e) => {
      if (sidebarOpen && !e.target.closest('.sidebar') && !e.target.closest('.hamburger-btn')) {
        setSidebarOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, sidebarOpen]);

  // Function to change language and trigger re-render
  const changeLanguage = (newLang) => {
    setLang(newLang);
    setGlobalLanguage(newLang);
    // Force re-render of all components by updating a dummy state
    setMessages(prev => [...prev]);
  };

  // Function to handle terms and privacy acceptance
  const handleAcceptTerms = () => {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('termsAccepted', 'true');
    }
    setShowConsentModal(false);
  };
  function handleMenuSelect(idx, action) {
    setSelectedMenu(idx);
    setTimeout(() => setSelectedMenu(null), 1200); // désélectionne après 1.2s
    action();
  }

  // Charger les conversations à la connexion

  const fetchConvs = React.useCallback(async () => {
    try {
      const session = await supabase.auth.getSession();
      const accessToken = session.data?.session?.access_token;
      const res = await fetch('/api/conversations_list', {
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: 'include'
      });
      if (!res.ok) {
        throw new Error(`HTTP error: ${res.status}`);
      }
      const data = await res.json();
      if (data.success) setConversations(data.conversations);
      else {
        alert(t('chat.networkError'));
      }
    } catch (error) {
      console.error('Error while fetching conversations:', error);
      alert(t('chat.networkError'));
    }
  }, [t, supabase]);

  useEffect(() => {
    if (!user) return;
    fetchConvs();
  }, [user, fetchConvs]);

  // Créer une nouvelle conversation (frontend only, pas d'appel API tant qu'aucun message)
  const handleCreateConversation = () => {
    setSelectedConversation(null);
    setMessages([]);
    setCurrentConversationId(null);
    // On ne crée une nouvelle conversation que si l'utilisateur le demande explicitement
  };

  // Sélectionner une conversation
  const handleSelectConversation = async (conv) => {
    setSelectedConversation(conv);
    if (!user) return;
    const session = await supabase.auth.getSession();
    const accessToken = session.data?.session?.access_token;
    const res = await fetch(`/api/conversations-api?action=get&id=${conv.id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      credentials: 'include'
    });
    const data = await res.json();
    if (data.success) {
      setMessages(data.conversation.messages || []);
    }
  };

  // Filtrage recherche
  const filteredConversations = conversations.filter(conv =>
    (conv.title || '').toLowerCase().includes(sidebarSearch.toLowerCase())
  );

  // Renommer une conversation
  const handleRenameConversation = async (convId, newTitle) => {
    if (!user || !convId || !newTitle.trim()) return;
    const session = await supabase.auth.getSession();
    const accessToken = session.data?.session?.access_token;
    const res = await fetch(`/api/conversations-api?action=rename&id=${convId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`
      },
      body: JSON.stringify({ title: newTitle }),
      credentials: 'include'
    });
    const data = await res.json();
    if (data.success && data.conversation) {
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, title: data.conversation.title, updated_at: data.conversation.updated_at } : c));
    }
  };

  // Supprimer une conversation
  const handleDeleteConversation = async (convId) => {
    if (!user || !convId) return;
    try {
      const session = await supabase.auth.getSession();
      const accessToken = session.data?.session?.access_token;
      const res = await fetch(`/api/conversations-api?action=delete&id=${convId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        credentials: 'include'
      });
      if (!res.ok) {
        // HTTP error (e.g. 401, 500, CORS, etc)
        throw new Error(`HTTP error: ${res.status}`);
      }
      const data = await res.json();
      if (data.success) {
        setConversations(prev => prev.filter(c => c.id !== convId));
        if (selectedConversation && selectedConversation.id === convId) {
          setSelectedConversation(null);
          setMessages([]);
        }
      } else {
        // API response but not successful
        alert(t('chat.deleteError') + (data.error || t('chat.error')));
      }
    } catch (error) {
      // Network or fetch error
      console.error('Error while deleting:', error);
      alert(t('chat.networkError'));
    }
  };
  // Vérifie la session utilisateur au chargement
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: { session } } = await supabase.auth.getSession();
      setUser(user);
      
      // Vérifier si l'utilisateur est banni dès qu'il est récupéré (via API)
      if (user && session) {
        const isBanned = await checkBannedUserAPI(user, session);
        if (isBanned) {
          console.log('🚫 Utilisateur banni détecté dès la connexion');
          setShowBannedModal(true);
          return; // Arrêter le processus ici
        }
      }
      
      // Détection automatique de la langue via Discord si c'est une nouvelle connexion
      if (user && user.user_metadata) {
        const currentStoredLang = localStorage.getItem('lang');
        // Cherche la locale dans user_metadata.locale ou custom_claims
        const discordLocale = user.user_metadata.locale || user.user_metadata.custom_claims?.locale;
        const detectedLang = mapDiscordLocaleToLanguage(discordLocale);
        
        // console.log('Discord locale détectée:', discordLocale, '-> langue:', detectedLang);
        
        // Applique la langue détectée seulement si aucune langue n'est stockée ou si c'est différent
        if (detectedLang && (!currentStoredLang || currentStoredLang === 'en')) {
          // console.log('Application de la langue détectée:', detectedLang);
          changeLanguage(detectedLang);
        }
      }
    };
    getUser();
    // Écoute les changements de session
    const { data: listener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const user = session?.user ?? null;
      setUser(user);
      
      // Vérifier si l'utilisateur est banni dès la connexion (via API)
      if (user && session) {
        const isBanned = await checkBannedUserAPI(user, session);
        if (isBanned) {
          console.log('🚫 Utilisateur banni détecté lors du changement de session');
          setShowBannedModal(true);
          return; // Arrêter le processus ici
        }
      }
      
      // Détection automatique de la langue lors de la connexion
      if (user && user.user_metadata) {
        const currentStoredLang = localStorage.getItem('lang');
        // Cherche la locale dans user_metadata.locale ou custom_claims
        const discordLocale = user.user_metadata.locale || user.user_metadata.custom_claims?.locale;
        const detectedLang = mapDiscordLocaleToLanguage(discordLocale);
        
        console.log('Discord locale détectée (connexion):', discordLocale, '-> langue:', detectedLang);
        
        // Applique la langue détectée seulement si aucune langue n'est stockée ou si c'est la langue par défaut
        if (detectedLang && (!currentStoredLang || currentStoredLang === 'en')) {
          console.log('Application de la langue détectée (connexion):', detectedLang);
          changeLanguage(detectedLang);
        }
      }
    });
    return () => {
      listener?.subscription.unsubscribe();
    };
  }, []);

  // Connexion OAuth Discord via Supabase
  const handleDiscordLogin = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'discord',
      options: {
        redirectTo: window.location.origin
      }
    });
  };

  // Déconnexion
  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState({});

  // ...existing code...

  // Ajout du paramètre captchaToken
  const sendMessage = async (captchaToken) => {
    if ((!inputMessage.trim() && selectedFiles.length === 0) || isLoading) return;
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    const userMessage = { role: 'user', content: inputMessage, id: Date.now(), files: selectedFiles };
    const currentMessage = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    // Mettre à jour les messages avec le nouveau message utilisateur
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);

    // Correction : on affiche uniquement le texte généré (champ 'response' de l'API)
    const assistantMessageId = Date.now() + 1;
    setMessages(prev => [...prev, { role: 'assistant', content: '', id: assistantMessageId }]);

    const conversationHistory = updatedMessages.map(msg => ({
      role: msg.role,
      content: msg.content
    }));
    const session = await supabase.auth.getSession();
    const accessToken = session.data?.session?.access_token;

    // Convertir les images en base64 si présentes
    let images = [];
    if (selectedFiles.length > 0) {
      for (const file of selectedFiles) {
        if (file.type && file.type.startsWith('image/')) {
          try {
            const base64 = await fileToBase64(file);
            // Retirer le préfixe data:image/...;base64,
            const base64Data = base64.split(',')[1];
            images.push({
              data: base64Data,
              mimeType: file.type
            });
          } catch (error) {
            console.error('Erreur conversion base64:', error);
          }
        }
      }
    }

    // Toujours envoyer en JSON maintenant
    const chatPayload = {
      message: currentMessage,
      model: 'gemini-2.5-flash',
      stream: false,
      conversationHistory: conversationHistory,
      images: images
    };
    if (currentConversationId) {
      chatPayload.id = currentConversationId;
    }

    const fetchOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        ...(captchaToken ? { 'cf-turnstile-response': captchaToken } : {})
      },
      body: JSON.stringify(chatPayload),
      credentials: 'include'
    };

    try {
        const res = await fetch('/api/chat', fetchOptions);
        
        // Vérifier si l'utilisateur est banni
        if (res.status === 403) {
          const errorData = await res.json();
          if (errorData.error === 'BANNED_USER') {
            setShowBannedModal(true);
            setIsLoading(false);
            setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId)); // Retirer le message assistant vide
            return;
          }
        }
        
        // Vérifier si la réponse est OK
        if (res.ok) {
          setIsLoading(false);
          
          // Mode JSON : traiter la réponse JSON directement
          try {
            const data = await res.json();
            
            if (data.success && data.response) {
              // Mettre à jour le message avec la réponse complète
              setMessages(prev => {
                return prev.map(msg => {
                  if (msg.id === assistantMessageId) {
                    return { ...msg, content: data.response };
                  }
                  return msg;
                });
              });
              
              console.log('✅ Réponse reçue et affichée');
              
              // Sauvegarder la conversation complète
              let convId = currentConversationId;
              // Obtenir tous les messages actuels plus la réponse de l'assistant
              setMessages(prev => {
                const allMessages = [...prev.filter(msg => msg.id !== assistantMessageId), { role: 'assistant', content: data.response, id: assistantMessageId }];
                
                // Sauvegarder la conversation avec tous les messages
                const savePayload = {
                  messages: allMessages,
                  model: 'gemini-2.5-flash',
                };
                if (convId) {
                  savePayload.id = convId;
                }
                
                // Faire la sauvegarde en arrière-plan
                fetch('/api/conversations', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {})
                  },
                  body: JSON.stringify(savePayload),
                  credentials: 'include'
                })
                .then(async (saveRes) => {
                  if (saveRes.ok) {
                    const saveData = await saveRes.json();
                    if (saveData.success && saveData.conversation && !convId) {
                      // C'est une nouvelle conversation, mettre à jour les states
                      const newConversation = saveData.conversation;
                      setCurrentConversationId(newConversation.id);
                      setSelectedConversation(newConversation);
                      console.log('✅ Nouvelle conversation créée avec ID:', newConversation.id);
                    }
                    console.log('✅ Conversation sauvegardée');
                    
                    // Actualiser la liste des conversations après la sauvegarde
                    if (typeof fetchConvs === 'function') {
                      fetchConvs();
                    }
                  }
                })
                .catch((e) => {
                  console.error('Erreur lors de la sauvegarde:', e);
                });
                
                return prev.map(msg => {
                  if (msg.id === assistantMessageId) {
                    return { ...msg, content: data.response };
                  }
                  return msg;
                });
              });
              
            } else {
              throw new Error(data.error || 'Erreur lors de la génération de la réponse');
            }
          } catch (jsonError) {
            console.error('❌ Erreur lors du traitement de la réponse JSON:', jsonError);
            setMessages(prev => prev.map((msg) =>
              msg.id === assistantMessageId
                ? { ...msg, content: t('chat.streamingError'), role: 'error' }
                : msg
            ));
          }
        } else {
          // Si la réponse n'est pas OK, traiter l'erreur
          const errorData = await res.json();
          throw new Error(errorData.error || 'Erreur du serveur');
        }
    } catch (error) {
      console.error('Erreur:', error);
      setMessages(prev => prev.map((msg) =>
        msg.id === assistantMessageId
          ? { ...msg, content: t('chat.error'), role: 'error' }
          : msg
      ));
    } finally {
      setIsLoading(false);
      setSelectedFiles([]);
      setFilePreviews([]);
    }
  };

  // Fonctions pour les boutons d'action
  const copyToClipboard = async (text, messageId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(prev => ({ ...prev, [messageId]: t('chat.copied') }));
      setTimeout(() => {
        setCopyFeedback(prev => {
          const newState = { ...prev };
          delete newState[messageId];
          return newState;
        });
      }, 2000);
      console.log('Message copied to clipboard');
    } catch (err) {
      console.error('Erreur lors de la copie :', err);
      // Fallback pour les navigateurs plus anciens
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopyFeedback(prev => ({ ...prev, [messageId]: t('chat.copied') }));
        setTimeout(() => {
          setCopyFeedback(prev => {
            const newState = { ...prev };
            delete newState[messageId];
            return newState;
          });
        }, 2000);
        console.log('Message copied to clipboard (fallback)');
      } catch (fallbackErr) {
        setCopyFeedback(prev => ({ ...prev, [messageId]: t('chat.copyError') }));
        setTimeout(() => {
          setCopyFeedback(prev => {
            const newState = { ...prev };
            delete newState[messageId];
            return newState;
          });
        }, 2000);
        console.error('Impossible de copier le texte', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const regenerateMessage = async (assistantMessageIndex) => {
    if (isLoading) return;
    
    // Supprimer le message IA à régénérer et tous les suivants
    const messagesUntilRegeneration = messages.slice(0, assistantMessageIndex);
    setMessages(messagesUntilRegeneration);
    setIsLoading(true);

    // Trouver le message utilisateur qui correspond à cette réponse IA
    let userMessageContent = '';
    for (let i = assistantMessageIndex - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        userMessageContent = messages[i].content;
        break;
      }
    }
    
    if (!userMessageContent) {
      console.error('Impossible de trouver le message utilisateur correspondant');
      setIsLoading(false);
      return;
    }

    // Ajouter un nouveau message assistant vide pour la réponse
    const assistantMessageId = Date.now();
    const initialAssistantMessage = { role: 'assistant', content: '', id: assistantMessageId };
    setMessages(prev => [...prev, initialAssistantMessage]);

    // Préparer l'historique pour l'API (tout sauf le message assistant qu'on vient d'ajouter)
    const conversationHistory = messagesUntilRegeneration.map(msg => ({
      role: msg.role,
      content: msg.content
    }));

    console.log('🔄 Regeneration:', {
      userMessage: userMessageContent,
      historyLength: conversationHistory.length,
      model: 'gemini-2.5-flash'
    });

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          message: userMessageContent,
          model: 'gemini-2.5-flash',
          stream: false,
          conversationHistory: conversationHistory
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Erreur du serveur');
      }

      // Mode JSON : traiter la réponse JSON directement
      try {
        const data = await response.json();
        
        if (data.success && data.response) {
          // Mettre à jour le message avec la réponse complète
          setMessages(prev => {
            const updatedMessages = prev.map((msg) =>
              msg.id === assistantMessageId 
                ? { ...msg, content: data.response }
                : msg
            );
            
            // Sauvegarder la conversation après régénération
            const convId = currentConversationId;
            if (convId) {
              // Récupérer le token d'accès pour la sauvegarde
              supabase.auth.getSession().then(session => {
                const accessToken = session.data?.session?.access_token;
                
                const savePayload = {
                  id: convId,
                  messages: updatedMessages,
                  model: 'gemini-2.5-flash',
                };
                
                // Sauvegarder en arrière-plan
                fetch('/api/conversations', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {})
                  },
                  body: JSON.stringify(savePayload),
                  credentials: 'include'
                })
                .then(() => {
                  console.log('✅ Conversation sauvegardée après régénération');
                  // Rafraîchir la liste des conversations
                  if (typeof fetchConvs === 'function') fetchConvs();
                })
                .catch((e) => {
                  console.error('Erreur lors de la sauvegarde après régénération:', e);
                });
              });
            }
            
            return updatedMessages;
          });
          
          console.log('✅ Régénération terminée et affichée');
          
        } else {
          throw new Error(data.error || 'Erreur lors de la régénération');
        }
      } catch (jsonError) {
        console.error('❌ Erreur lors du traitement de la réponse JSON de régénération:', jsonError);
        setMessages(prev => prev.map((msg) => 
          msg.id === assistantMessageId 
            ? { ...msg, content: t('chat.regenerateError'), role: 'error' }
            : msg
        ));
      }

    } catch (error) {
      console.error('Erreur:', error);
    setMessages(prev => prev.map((msg) => 
      msg.id === assistantMessageId 
        ? { ...msg, content: t('chat.regenerateError'), role: 'error' }
        : msg
    ));
    } finally {
      setIsLoading(false);
    }
  };

  const shareMessage = async (text, messageId) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('chat.shareTitle'),
          text: text,
        });
        console.log('Message shared successfully');
      } catch (err) {
        console.error('Erreur lors du partage :', err);
        copyToClipboard(text, messageId);
      }
    } else {
      // Fallback : copier dans le presse-papiers
      copyToClipboard(text, messageId);
      console.log('Sharing not supported, text copied to clipboard');
    }
  };

  // Gestion des fichiers déplacée dans ChatInput

  // Dropdown menu state
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const headerMenuRef = useRef();
  useEffect(() => {
    function handleClickOutside(event) {
      if (headerMenuRef.current && !headerMenuRef.current.contains(event.target)) {
        setHeaderMenuOpen(false);
      }
    }
    if (headerMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [headerMenuOpen]);

  return (
    <div className="chatgpt-app">
      {/* Mobile header */}
      {isMobile && (
        <div className="mobile-header">
          <button 
            className="hamburger-btn" 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          
          <h1 style={{ 
            fontSize: '1.4rem', 
            fontWeight: '700', 
            color: '#fff', 
            margin: 0,
            letterSpacing: '0.5px',
            flex: 1,
            textAlign: 'center'
          }}>
            VolcanAI
          </h1>
          
          {/* User menu mobile */}
          {user ? (
            <div className="header-user mobile-user" onClick={() => setHeaderMenuOpen(!headerMenuOpen)}>
              <div className="header-user-avatar" style={{ width: '36px', height: '36px' }}>
                {user.user_metadata?.avatar_url ? (
                  <img src={user.user_metadata.avatar_url} alt="Avatar" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                ) : (
                  <div style={{ fontSize: '18px' }}>👤</div>
                )}
              </div>
              {/* Mobile user menu */}
              <AnimatePresence>
                {headerMenuOpen && (
                  <motion.div
                    className="header-menu-popup"
                    initial={{ opacity: 0, scale: 0.8, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.8, y: -10 }}
                  >
                    <MenuButton selected={selectedMenu === 0} onClick={() => handleMenuSelect(0, handleLogout)}>{t('userMenu.logout')}</MenuButton>
                    <MenuButton selected={selectedMenu === 1} onClick={() => handleMenuSelect(1, () => window.open('https://discord.gg/DSbUrfRyRY', '_blank'))}>{t('userMenu.supportServer')}</MenuButton>
                    <MenuButton selected={selectedMenu === 2} onClick={() => handleMenuSelect(2, () => window.open('/terms.html', '_blank'))}>{t('userMenu.termsOfUse')}</MenuButton>
                    <MenuButton selected={selectedMenu === 3} onClick={() => handleMenuSelect(3, () => window.open('/privacy.html', '_blank'))}>{t('userMenu.confidentiality')}</MenuButton>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <button className="login-btn-top mobile-login" onClick={handleDiscordLogin} style={{ 
              fontSize: '0.85rem', 
              padding: '8px 12px',
              borderRadius: '8px',
              background: '#1F1F1F',
              border: '1px solid #333',
              color: '#fff'
            }}>
              {t('loginButton.discord')}
            </button>
          )}
        </div>
      )}

      {/* Sidebar overlay for mobile */}
      {isMobile && (
        <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar conversations */}
      <Sidebar
        user={user}
        conversations={filteredConversations}
        onSelect={(conv) => {
          handleSelectConversation(conv);
          if (isMobile) setSidebarOpen(false); // Close sidebar after selection on mobile
        }}
        onCreate={() => {
          handleCreateConversation();
          if (isMobile) setSidebarOpen(false);
        }}
        selectedId={selectedConversation?.id}
        onSearch={() => {}}
        searchValue={sidebarSearch}
        setSearchValue={setSidebarSearch}
        onRename={handleRenameConversation}
        onDelete={handleDeleteConversation}
        className={isMobile ? (sidebarOpen ? 'open' : '') : ''}
        selectedModel={selectedModel}
        onModelSelect={setSelectedModel}
        lang={lang}
        onLanguageChange={changeLanguage}
        isMobile={isMobile}
      />
      {/* Menu déroulant des modèles avec animations (desktop only) */}
      {!isMobile && (
        <motion.div
          style={{ position: 'absolute', top: 0, left: 250, zIndex: 101, paddingTop: 18, paddingLeft: 18 }}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 320, damping: 22 }}
        >
          <ModelDropdownMenu
            selectedModel={selectedModel}
            onSelect={setSelectedModel}
            motionProps={{
              whileHover: { scale: 1.06, boxShadow: '0 2px 16px rgba(0,0,0,0.18)' },
              whileTap: { scale: 0.97 },
              initial: { opacity: 0, scale: 0.9 },
              animate: { opacity: 1, scale: 1 },
              exit: { opacity: 0, scale: 0.9 },
              transition: { type: 'spring', stiffness: 420, damping: 28 }
            }}
          />
        </motion.div>
      )}
      <LoginModal open={showLoginModal} onClose={() => setShowLoginModal(false)} onLogin={handleDiscordLogin} />
      
      {/* Modal de bannissement */}
      {showBannedModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            borderRadius: 12,
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            border: '1px solid #333',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                backgroundColor: '#ff4444',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '15px'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              </div>
              <h2 style={{
                color: '#fff',
                fontSize: '24px',
                fontWeight: '600',
                margin: 0
              }}>
                Account Banned
              </h2>
            </div>
            <p style={{
              color: '#ccc',
              fontSize: '16px',
              lineHeight: '1.5',
              marginBottom: '25px'
            }}>
              Your account has been banned from this service due to a violation of our terms of service. 
              If you wish to appeal this decision, please visit our support server.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => window.open('https://discord.gg/your-support-server', '_blank')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#5865f2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#4752c4'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#5865f2'}
              >
                Support Server
              </button>
              <button
                onClick={() => setShowBannedModal(false)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#444'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#333'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Consent Modal for Terms and Privacy */}
      <ConsentModal open={showConsentModal} onAccept={handleAcceptTerms} />

      {/* Menu utilisateur en haut à droite uniquement */}
      {/* Menu déroulant des modèles et sélecteur de langue (desktop only) */}
      {!isMobile && (
        <div style={{ position: 'absolute', top: 0, right: 0, zIndex: 100, paddingTop: 18, paddingRight: 18, display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Sélecteur de langue */}
          <select 
            value={lang} 
            onChange={(e) => changeLanguage(e.target.value)} 
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              border: '1.5px solid #333',
              background: '#18191c',
              color: '#fff',
              fontWeight: 500,
              fontSize: 15,
              cursor: 'pointer'
            }}
          >
            <option value="fr">{t('language.french')}</option>
            <option value="en">{t('language.english')}</option>
            <option value="es">{t('language.spanish')}</option>
            <option value="de">{t('language.german')}</option>
            <option value="it">{t('language.italian')}</option>
            <option value="nl">{t('language.dutch')}</option>
            <option value="pt">{t('language.portuguese')}</option>
            <option value="ru">{t('language.russian')}</option>
            <option value="ar">{t('language.arabic')}</option>
            <option value="zh">{t('language.mandarin')}</option>
            <option value="hi">{t('language.hindi')}</option>
            <option value="bn">{t('language.bengali')}</option>
            <option value="ja">{t('language.japanese')}</option>
            <option value="tr">{t('language.turkish')}</option>
            <option value="ko">{t('language.korean')}</option>
          </select>
          {/* User menu desktop */}
          {user ? (
            <div
              className={`header-user${headerMenuOpen ? ' active' : ''}`}
              onClick={() => setHeaderMenuOpen(v => !v)}
              tabIndex={0}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
            >
              <div className="header-user-avatar">
                {user.user_metadata?.avatar_url ? (
                  <img src={user.user_metadata.avatar_url} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '10px' }} />
                ) : (
                  <span style={{ color: '#222', fontWeight: 700, fontSize: 18 }}>{user.user_metadata?.display_name?.[0] || user.user_metadata?.name?.[0] || user.email?.[0]}</span>
                )}
              </div>
              <div className="header-user-name">
                {user.user_metadata?.custom_claims?.global_name 
                  ? user.user_metadata.custom_claims.global_name
                  : (user.user_metadata?.global_name || user.user_metadata?.display_name || user.user_metadata?.name || user.email)}
              </div>
              <AnimatePresence>
                {headerMenuOpen && (
                  <motion.div
                    ref={headerMenuRef}
                    className="header-menu-popup"
                    initial={{ opacity: 0, scale: 0.8, y: -10, backdropFilter: 'blur(0px)', WebkitBackdropFilter: 'blur(0px)' }}
                    animate={{ opacity: 1, scale: 1, y: 0, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', transition: { type: 'spring', stiffness: 400, damping: 22 } }}
                    exit={{ opacity: 0, scale: 0.8, y: -10, backdropFilter: 'blur(0px)', WebkitBackdropFilter: 'blur(0px)', transition: { duration: 0.18 } }}
                    style={{ border: '1.5px solid #363636', borderRadius: 10, background: '#23242a', boxShadow: '0 2px 16px rgba(0,0,0,0.18)', minWidth: 180, overflow: 'hidden', padding: 0 }}
                  >
                    <MenuButton selected={selectedMenu === 0} onClick={() => handleMenuSelect(0, handleLogout)}>{t('userMenu.logout')}</MenuButton>
                    <MenuButton selected={selectedMenu === 1} onClick={() => handleMenuSelect(1, () => window.open('https://discord.gg/DSbUrfRyRY', '_blank'))}>{t('userMenu.supportServer')}</MenuButton>
                    <MenuButton selected={selectedMenu === 2} onClick={() => handleMenuSelect(2, () => window.open('/terms.html', '_blank'))}>{t('userMenu.termsOfUse')}</MenuButton>
                    <MenuButton selected={selectedMenu === 3} onClick={() => handleMenuSelect(3, () => window.open('/privacy.html', '_blank'))}>{t('userMenu.confidentiality')}</MenuButton>
                    <MenuButton selected={selectedMenu === 4} onClick={() => handleMenuSelect(4, () => alert(t('notifications.settingsComingSoon')))}>{t('userMenu.settings')}</MenuButton>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <button className="login-btn-top" onClick={handleDiscordLogin}>
              {t('loginButton.discord')}
            </button>
          )}
        </div>
      )}

      {/* Liste des messages avec padding-top pour le header */}
      <MessagesList
        messages={messages}
        copyFeedback={copyFeedback}
        copyToClipboard={copyToClipboard}
        regenerateMessage={regenerateMessage}
        shareMessage={shareMessage}
        filePreviews={filePreviews}
        isLoading={isLoading}
        lang={lang}
        style={{ paddingTop: 80 }}
      />

      {/* Zone de saisie */}
      <ChatInput
        inputMessage={inputMessage}
        setInputMessage={setInputMessage}
        isLoading={isLoading}
        sendMessage={sendMessage}
        selectedFiles={selectedFiles}
        setSelectedFiles={setSelectedFiles}
        filePreviews={filePreviews}
        setFilePreviews={setFilePreviews}
        showRenameModal={showRenameModal}
        lang={lang}
      />

      {/* Bloc d'accueil supprimé, géré dans MessagesList.js */}
    </div>
  );
}

// Gestion de la sélection/désélection des boutons du menu utilisateur
function MenuButton({ children, onClick, selected }) {
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  let bgColor = 'none';
  if (selected) {
    bgColor = '#212121';
  } else if (active) {
    bgColor = '#1F1F1F';
  } else if (hover) {
    bgColor = '#282828';
  }
  return (
    <button
      className="header-menu-item"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{
        width: '100%',
        background: bgColor,
        border: 'none',
        color: '#fff',
        padding: '12px 18px',
        fontSize: 16,
        cursor: 'pointer',
        fontWeight: 500,
        transition: 'background 0.18s',
        outline: 'none',
        borderBottom: '1px solid #363636',
        textAlign: 'left',
      }}
    >
      {children}
    </button>
  );
}


export default App;
